/**
 * AI Assistant — page ⇄ dock bridge (G1: global shell).
 *
 * `EmployerLayout` mounts <CopilotProvider> once, so the provider exists on
 * every employer page. Pages publish what is on screen through
 * `useAssistantPage(spec)` (see useAssistantPage.js); the most recently
 * mounted page wins (a stack), so `ApplicationList` rendered inside the
 * Overview or My Jobs page takes over from the page below it and hands back
 * when it unmounts.
 *
 * The dock (`ApplicationsCopilot`), the structured blocks and the per-card
 * "Ask Copilot" button (`CandidateCardImproved`) all read the same context.
 *
 * `useCopilot()` returns a safe no-op default when rendered outside the
 * provider, so consumers can always call it unconditionally.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { pageFromRoute, routeScope as routeScopeOf } from './scope';

const noop = () => {};

export const DEFAULT_COPILOT_CONTEXT = Object.freeze({
  isDefault: true,
  page: 'other',
  activePage: null,
  location: null,
  routeScope: { type: 'company' },
  jobId: null,
  jobDetails: null,
  candidates: [],
  filters: {},
  selectedCandidate: null,
  isOpen: false,
  open: noop,
  close: noop,
  toggle: noop,
  askAbout: noop,
  pendingPrompt: null,
  consumePendingPrompt: () => null,
  clearSelection: noop,
  locateCandidate: noop,
  viewProfile: noop,
  applyFilter: noop,
  refreshCandidates: noop,
  navigateTo: () => false,
  openJob: () => false,
  uiContext: {},
});

const CopilotContext = createContext(DEFAULT_COPILOT_CONTEXT);

/** Stable registry handed to `useAssistantPage` (never changes identity). */
const DEFAULT_REGISTRY = Object.freeze({ registerPage: noop, unregisterPage: noop });
export const AssistantRegistryContext = createContext(DEFAULT_REGISTRY);

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/** Resolve the application id for a candidate row (`application_id ?? id`). */
export function getApplicationId(candidate) {
  if (!candidate) return null;
  const raw = candidate.application_id ?? candidate.applicationId ?? candidate.id ?? null;
  if (raw === null || raw === undefined || raw === '') return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : raw;
}

/** Find a candidate row by application id in a list. */
export function findCandidateByApplicationId(candidates, applicationId) {
  if (applicationId === null || applicationId === undefined) return null;
  const key = String(applicationId);
  return (candidates || []).find((c) => String(getApplicationId(c)) === key) || null;
}

const HIGHLIGHT_CLASSES = ['ring-2', 'ring-primary', 'ring-offset-2', 'rounded-lg', 'transition-shadow'];

/**
 * Scroll the card with `data-application-id="<id>"` into view and pulse a
 * ring around it for ~2 s. Returns true when the card was found on screen.
 */
export function locateApplicationCard(applicationId, { highlightMs = 2200 } = {}) {
  if (typeof document === 'undefined' || applicationId === null || applicationId === undefined) return false;
  const safeId = String(applicationId).replace(/["\\]/g, '');
  const el = document.querySelector(`[data-application-id="${safeId}"]`);
  if (!el) return false;
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  try {
    el.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
  } catch {
    el.scrollIntoView();
  }
  el.classList.add(...HIGHLIGHT_CLASSES);
  window.setTimeout(() => el.classList.remove(...HIGHLIGHT_CLASSES), highlightMs);
  return true;
}

/** Drop empty / "all" filter values so ui_context only carries active criteria. */
export function compactFilters(filters) {
  const out = {};
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined || value === 'all') return;
    out[key] = value;
  });
  return out;
}

function sameId(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
}

/**
 * Shallow "data" equality for page specs: functions are ignored (handlers
 * are read fresh from the registry at call time), arrays compare element by
 * element (pages often pass a fresh `.slice()` each render), everything else
 * by identity.
 */
export function specDataEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const va = a[key];
    const vb = b[key];
    if (typeof va === 'function' && typeof vb === 'function') continue;
    if (Array.isArray(va) && Array.isArray(vb)) {
      if (va.length !== vb.length) return false;
      for (let i = 0; i < va.length; i += 1) if (va[i] !== vb[i]) return false;
      continue;
    }
    if (va !== vb) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// navigation targets (pure, exported for the `navigate` block and tests)
// ---------------------------------------------------------------------------

export const APPLICATIONS_ROUTE = '/hirewithus/overview';
const ALLOWED_PATH_PREFIX = '/hirewithus/';

export function applicationsPath(jobId) {
  return `${APPLICATIONS_ROUTE}?view=applications&jobId=${encodeURIComponent(String(jobId))}`;
}

/**
 * Route for a `navigate` block / intent, or null when it must be ignored.
 *   job_applications → /hirewithus/overview?view=applications&jobId=<id>
 *   candidate        → same (the profile opens once the page has loaded)
 *   page             → `path`, only when it starts with /hirewithus/
 */
export function navigateTargetPath(target) {
  if (!target || typeof target !== 'object') return null;
  const kind = String(target.target || '');
  if (kind === 'job_applications' || kind === 'candidate') {
    const jobId = target.job_id ?? target.jobId;
    if (jobId === null || jobId === undefined || jobId === '') return null;
    if (kind === 'candidate' && (target.application_id === null || target.application_id === undefined)) return null;
    return applicationsPath(jobId);
  }
  if (kind === 'page') {
    const path = String(target.path || '');
    if (!path.startsWith(ALLOWED_PATH_PREFIX)) return null;
    if (/^\/\/|[\s<>"']|javascript:/i.test(path)) return null;
    return path;
  }
  return null;
}

// ---------------------------------------------------------------------------
// pending "open this candidate once the page loads" intent
// ---------------------------------------------------------------------------
// Module scope on purpose: each employer page renders its own <EmployerLayout>,
// so the provider remounts on route changes; the intent must outlive that.

export const PENDING_INTENT_TTL_MS = 15000;
let pendingIntent = null;

export function setPendingCandidateIntent(jobId, applicationId) {
  if (jobId === null || jobId === undefined || applicationId === null || applicationId === undefined) return;
  pendingIntent = { jobId, applicationId, at: Date.now() };
}

export function peekPendingCandidateIntent() {
  if (!pendingIntent) return null;
  if (Date.now() - pendingIntent.at > PENDING_INTENT_TTL_MS) {
    pendingIntent = null;
    return null;
  }
  return pendingIntent;
}

export function clearPendingCandidateIntent() {
  pendingIntent = null;
}

// ---------------------------------------------------------------------------
// provider
// ---------------------------------------------------------------------------

const OPEN_KEY = 'copilot.open';
const EMPTY_LIST = Object.freeze([]);
const EMPTY_FILTERS = Object.freeze({});

function readOpen() {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(value) {
  try {
    window.localStorage.setItem(OPEN_KEY, value ? '1' : '0');
  } catch {
    // storage unavailable
  }
}

function CopilotProviderCore({ location, navigate, children }) {
  const [isOpen, setIsOpen] = useState(readOpen);
  const [copilotSelected, setCopilotSelected] = useState(null);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const nonceRef = useRef(0);

  // ---- page registry (stack; last mounted wins) ---------------------------
  const pagesRef = useRef([]);
  const [pagesVersion, bumpPages] = useReducer((n) => n + 1, 0);

  const registerPage = useCallback((id, spec) => {
    const list = pagesRef.current;
    const idx = list.findIndex((p) => p.id === id);
    const next = spec && typeof spec === 'object' ? spec : {};
    if (idx === -1) {
      pagesRef.current = [...list, { id, spec: next }];
      bumpPages();
      return;
    }
    const prev = list[idx].spec;
    list[idx] = { id, spec: next }; // always keep the freshest handlers
    if (!specDataEqual(prev, next)) bumpPages();
  }, []);

  const unregisterPage = useCallback((id) => {
    const list = pagesRef.current;
    if (!list.some((p) => p.id === id)) return;
    pagesRef.current = list.filter((p) => p.id !== id);
    bumpPages();
  }, []);

  const registry = useMemo(() => ({ registerPage, unregisterPage }), [registerPage, unregisterPage]);

  const getActive = useCallback(() => {
    const list = pagesRef.current;
    return list.length ? list[list.length - 1].spec : null;
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activePage = useMemo(() => getActive(), [pagesVersion, getActive]);

  const routeScope = useMemo(() => routeScopeOf(location), [location]);
  const page = activePage?.page || pageFromRoute(location);

  // Applications-page facts (null/empty on other pages).
  const pageFacts = useMemo(() => {
    const isApplications = activePage?.page === 'applications';
    return {
      jobId: isApplications ? activePage.jobId ?? null : routeScope.type === 'job' ? routeScope.job_id : null,
      jobDetails: isApplications ? activePage.jobDetails || null : null,
      candidates: isApplications && Array.isArray(activePage.candidates) ? activePage.candidates : EMPTY_LIST,
      filters: isApplications && activePage.filters ? activePage.filters : EMPTY_FILTERS,
      pageSelectedCandidate: isApplications ? activePage.selectedCandidate || null : null,
    };
  }, [activePage, routeScope]);
  const { jobId, jobDetails, candidates, filters, pageSelectedCandidate } = pageFacts;

  // ---- open / close (persisted across route remounts) ---------------------
  useEffect(() => {
    writeOpen(isOpen);
  }, [isOpen]);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Drop a copilot-side selection that belongs to another job's page.
  useEffect(() => {
    setCopilotSelected(null);
  }, [jobId]);

  const selectedCandidate = copilotSelected || pageSelectedCandidate || null;

  const askAbout = useCallback((candidate, prompt) => {
    if (!candidate) return;
    nonceRef.current += 1;
    setCopilotSelected(candidate);
    setPendingPrompt({
      nonce: nonceRef.current,
      candidate,
      applicationId: getApplicationId(candidate),
      name: candidate.name || candidate.candidate_name || 'this candidate',
      prompt: prompt || null,
    });
    setIsOpen(true);
  }, []);

  const consumePendingPrompt = useCallback(() => {
    let taken = null;
    setPendingPrompt((current) => {
      taken = current;
      return null;
    });
    return taken;
  }, []);

  const clearSelection = useCallback(() => setCopilotSelected(null), []);

  // ---- navigation -----------------------------------------------------------
  const goTo = useCallback(
    (path) => {
      if (!path) return false;
      if (typeof navigate === 'function') navigate(path);
      else if (typeof window !== 'undefined') window.location.assign(path);
      return true;
    },
    [navigate]
  );

  const viewProfileHere = useCallback(
    (applicationId) => {
      const spec = getActive();
      if (!spec || spec.page !== 'applications' || typeof spec.onViewProfile !== 'function') return false;
      const candidate = findCandidateByApplicationId(spec.candidates, applicationId);
      if (!candidate) return false;
      spec.onViewProfile(applicationId, candidate);
      return true;
    },
    [getActive]
  );

  /**
   * Route a `navigate` block: `{ target, job_id, application_id, path, label }`.
   * Returns true when something happened.
   */
  const navigateTo = useCallback(
    (target) => {
      const path = navigateTargetPath(target);
      if (!path) return false;
      if (target.target === 'candidate') {
        const spec = getActive();
        const onThatJob = spec?.page === 'applications' && sameId(spec.jobId, target.job_id);
        if (onThatJob && viewProfileHere(target.application_id)) return true;
        setPendingCandidateIntent(target.job_id, target.application_id);
        if (onThatJob) return true; // candidate list still loading; the intent fires when it arrives
      }
      return goTo(path);
    },
    [getActive, goTo, viewProfileHere]
  );

  const openJob = useCallback((targetJobId) => navigateTo({ target: 'job_applications', job_id: targetJobId }), [navigateTo]);

  /** `otherJobId` (optional) is the job the candidate belongs to; another job → navigate first. */
  const viewProfile = useCallback(
    (applicationId, otherJobId) => {
      const spec = getActive();
      const hereJobId = spec?.page === 'applications' ? spec.jobId : null;
      const crossJob = otherJobId !== undefined && otherJobId !== null && !sameId(otherJobId, hereJobId);
      if (crossJob) return navigateTo({ target: 'candidate', job_id: otherJobId, application_id: applicationId });
      return viewProfileHere(applicationId);
    },
    [getActive, navigateTo, viewProfileHere]
  );

  const locateCandidate = useCallback(
    (applicationId, otherJobId) => {
      const spec = getActive();
      const hereJobId = spec?.page === 'applications' ? spec.jobId : null;
      const crossJob = otherJobId !== undefined && otherJobId !== null && !sameId(otherJobId, hereJobId);
      if (crossJob || spec?.page !== 'applications') {
        if (otherJobId === undefined || otherJobId === null) return false;
        return navigateTo({ target: 'candidate', job_id: otherJobId, application_id: applicationId });
      }
      const found =
        typeof spec.onLocate === 'function' ? spec.onLocate(applicationId) : locateApplicationCard(applicationId);
      if (found !== false) return true;
      // Card is on another page / filtered out: fall back to opening the profile.
      return viewProfileHere(applicationId);
    },
    [getActive, navigateTo, viewProfileHere]
  );

  const applyFilter = useCallback(
    (filterObj) => {
      const spec = getActive();
      if (!spec || typeof spec.onApplyFilter !== 'function' || !filterObj) return;
      spec.onApplyFilter(filterObj);
    },
    [getActive]
  );

  // Re-fetch the page's applicant list (after the assistant scheduled a test, etc.).
  const refreshCandidates = useCallback(() => {
    const spec = getActive();
    if (spec && typeof spec.onRefresh === 'function') spec.onRefresh();
  }, [getActive]);

  // ---- pending candidate intent: fire once the right page has its rows -----
  useEffect(() => {
    const intent = peekPendingCandidateIntent();
    if (!intent) return;
    const spec = getActive();
    if (!spec || spec.page !== 'applications' || !sameId(spec.jobId, intent.jobId)) return;
    if (viewProfileHere(intent.applicationId)) clearPendingCandidateIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesVersion, getActive, viewProfileHere]);

  // ---- ui_context -------------------------------------------------------------
  const uiContext = useMemo(() => {
    const spec = activePage;
    if (!spec) return {};
    if (spec.page === 'applications') {
      const visible = Array.isArray(spec.visibleCandidates) ? spec.visibleCandidates : spec.candidates || [];
      const visibleIds = (visible || [])
        .map(getApplicationId)
        .filter((id) => id !== null && id !== undefined)
        .slice(0, 200);
      const selectedId = getApplicationId(selectedCandidate);
      const ctx = {
        active_tab: spec.mainTab || 'applicants',
        view_mode: spec.viewMode || 'list',
        filters: compactFilters(spec.filters),
        search_query: spec.searchQuery || '',
        visible_application_ids: visibleIds,
      };
      if (selectedId !== null) ctx.selected_application_id = selectedId;
      return ctx;
    }
    if (spec.page === 'my-jobs' && Array.isArray(spec.jobs)) {
      return {
        visible_job_ids: spec.jobs
          .map((j) => j?.job_id ?? j?.id)
          .filter((id) => id !== null && id !== undefined)
          .slice(0, 100),
      };
    }
    // G2 adapters: the offer & onboarding rows on screen / the open WhatsApp thread.
    if (spec.page === 'hiring-lifecycle' && Array.isArray(spec.candidates)) {
      return {
        visible_application_ids: spec.candidates
          .map(getApplicationId)
          .filter((id) => id !== null && id !== undefined)
          .slice(0, 200),
      };
    }
    if (spec.page === 'messages' && spec.activeConversation && typeof spec.activeConversation === 'object') {
      const { candidate_email: email, name, phone } = spec.activeConversation;
      const conv = {};
      if (email) conv.candidate_email = email;
      if (name) conv.name = name;
      if (phone) conv.phone = phone;
      return Object.keys(conv).length ? { active_conversation: conv } : {};
    }
    return {};
  }, [activePage, selectedCandidate]);

  const value = useMemo(
    () => ({
      isDefault: false,
      page,
      activePage,
      location,
      routeScope,
      jobId,
      jobDetails,
      candidates,
      filters,
      selectedCandidate,
      isOpen,
      open,
      close,
      toggle,
      askAbout,
      pendingPrompt,
      consumePendingPrompt,
      clearSelection,
      locateCandidate,
      viewProfile,
      applyFilter,
      refreshCandidates,
      navigateTo,
      openJob,
      uiContext,
    }),
    [
      page,
      activePage,
      location,
      routeScope,
      jobId,
      jobDetails,
      candidates,
      filters,
      selectedCandidate,
      isOpen,
      open,
      close,
      toggle,
      askAbout,
      pendingPrompt,
      consumePendingPrompt,
      clearSelection,
      locateCandidate,
      viewProfile,
      applyFilter,
      refreshCandidates,
      navigateTo,
      openJob,
      uiContext,
    ]
  );

  return (
    <AssistantRegistryContext.Provider value={registry}>
      <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>
    </AssistantRegistryContext.Provider>
  );
}

function RoutedCopilotProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <CopilotProviderCore location={location} navigate={navigate}>
      {children}
    </CopilotProviderCore>
  );
}

/**
 * Mount once per employer layout. Works with or without a Router above it
 * (outside a Router the dock falls back to `window.location` for navigation
 * and the company scope).
 */
export function CopilotProvider({ children }) {
  const inRouter = useInRouterContext();
  if (inRouter) return <RoutedCopilotProvider>{children}</RoutedCopilotProvider>;
  return (
    <CopilotProviderCore location={null} navigate={null}>
      {children}
    </CopilotProviderCore>
  );
}

export function useCopilot() {
  return useContext(CopilotContext) || DEFAULT_COPILOT_CONTEXT;
}

export function useAssistantRegistry() {
  return useContext(AssistantRegistryContext) || DEFAULT_REGISTRY;
}

export default CopilotContext;
